/**
 * LegalHoldsListView — Lista de Legal Holds ativos.
 *
 * D-B defense-in-depth: gates lgpd-legal-holds-manage independente
 * do parent gate. P17: toda ação muta estado + emite audit (server-side).
 * P15: entityId mascarado parcialmente na listagem.
 *
 * Brief 5 B1 — tab "Legal Holds".
 */

import { CapabilityLockedBanner } from '@lume/ui-components';

import { CapabilityGate } from '../../../components/CapabilityGate';
import { useCapability } from '../../../hooks/use-capability';
import { useLegalHolds, useLiftLegalHold } from '../../../hooks/use-lgpd-legal-holds';

import type { LegalHoldWireItem } from './types';

interface LegalHoldsListViewProps {
  /** Filter by status; defaults to showing active */
  filterStatus?: 'active' | 'lifted' | 'all';
}

const HOLD_TYPE_LABEL: Record<string, string> = {
  judicial: 'Judicial',
  administrative: 'Administrativo',
  investigation: 'Investigação',
  other: 'Outro',
};

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  active: { label: 'Ativo', bg: '#fef2f2', color: '#b91c1c' },
  lifted: { label: 'Levantado', bg: '#f0fdf4', color: '#166534' },
  expired: { label: 'Expirado', bg: '#f9fafb', color: '#6b7280' },
};

export function LegalHoldsListView({
  filterStatus = 'active',
}: LegalHoldsListViewProps): JSX.Element {
  const canManage = useCapability('lgpd-legal-holds-manage');

  if (!canManage) {
    return (
      <CapabilityLockedBanner
        capability="lgpd-legal-holds-manage"
        tierMin="professional"
        fallbackMessage="A gestão de Legal Holds é restrita ao DPO (disponível a partir do pacote Professional)."
      />
    );
  }

  return <LegalHoldsListContent filterStatus={filterStatus} />;
}

function LegalHoldsListContent({
  filterStatus,
}: {
  filterStatus: 'active' | 'lifted' | 'all';
}): JSX.Element {
  const resolvedStatus = filterStatus === 'all' ? undefined : filterStatus;
  const { data, isLoading, isError } = useLegalHolds(
    resolvedStatus !== undefined ? { status: resolvedStatus } : {},
  );
  const liftMutation = useLiftLegalHold();

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Carregando Legal Holds...">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              height: 72,
              borderRadius: 8,
              backgroundColor: '#f3f4f6',
              marginBottom: 8,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#ef4444', fontSize: 13 }}>
        Erro ao carregar Legal Holds. Tente recarregar.
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        style={{
          padding: '40px 24px',
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 13,
        }}
      >
        {filterStatus === 'active'
          ? 'Nenhum Legal Hold ativo no momento.'
          : 'Nenhum Legal Hold encontrado.'}
      </div>
    );
  }

  return (
    <div
      role="list"
      aria-label="Legal Holds"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {data.map((hold) => (
        <LegalHoldRow
          key={hold.id}
          hold={hold}
          onLift={(liftReason) => {
            liftMutation.mutate({ holdId: hold.id, liftReason });
          }}
          isLifting={liftMutation.isPending}
        />
      ))}
    </div>
  );
}

// ─── LegalHoldRow ─────────────────────────────────────────────────────────────

function LegalHoldRow({
  hold,
  onLift,
  isLifting,
}: {
  hold: LegalHoldWireItem;
  onLift: (reason: string) => void;
  isLifting: boolean;
}): JSX.Element {
  const statusCfg = STATUS_CONFIG[hold.status] ?? {
    label: hold.status,
    bg: '#f9fafb',
    color: '#6b7280',
  };
  const holdTypeLabel = HOLD_TYPE_LABEL[hold.holdType] ?? hold.holdType;

  // P15 minimização: mascarar entityId parcialmente
  const entityIdMasked =
    hold.entityId.length > 8
      ? `${hold.entityId.slice(0, 4)}…${hold.entityId.slice(-4)}`
      : hold.entityId;

  const isExpiringSoon =
    hold.expiresAt !== null &&
    new Date(hold.expiresAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;

  return (
    <div
      role="listitem"
      style={{
        padding: '12px 16px',
        background: '#fff',
        border: '1px solid',
        borderColor: hold.status === 'active' ? '#fca5a5' : '#e5e7eb',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      {/* Tipo icon */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: hold.status === 'active' ? '#fef2f2' : '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        {hold.holdType === 'judicial' ? '⚖️' : hold.holdType === 'investigation' ? '🔍' : '📋'}
      </div>

      {/* Conteúdo */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#111827',
            marginBottom: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {holdTypeLabel}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              background: statusCfg.bg,
              color: statusCfg.color,
            }}
          >
            {statusCfg.label}
          </span>
          {isExpiringSoon && hold.status === 'active' && (
            <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>
              ⚠ Expira em breve
            </span>
          )}
        </div>

        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontFamily: 'monospace' }}>
          {hold.entityType} · {entityIdMasked}
          {hold.reference !== null && ` · Ref: ${hold.reference}`}
        </div>

        <div
          style={{
            fontSize: 12,
            color: '#374151',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {hold.reason}
        </div>

        {hold.expiresAt !== null && (
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
            Expira em {new Date(hold.expiresAt).toLocaleDateString('pt-BR')}
          </div>
        )}
      </div>

      {/* Ação levantar hold — D-B: gate capability lgpd-legal-holds-manage */}
      {hold.status === 'active' && (
        <CapabilityGate capability="lgpd-legal-holds-manage">
          <button
            type="button"
            disabled={isLifting}
            onClick={() => {
              const reason = window.prompt('Motivo para levantar o hold (obrigatório):');
              if (reason !== null && reason.trim() !== '') {
                onLift(reason.trim());
              }
            }}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#374151',
              fontSize: 11,
              fontWeight: 500,
              cursor: isLifting ? 'not-allowed' : 'pointer',
              opacity: isLifting ? 0.6 : 1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            aria-label="Levantar legal hold"
          >
            {isLifting ? 'Processando…' : 'Levantar hold'}
          </button>
        </CapabilityGate>
      )}
    </div>
  );
}
